'use client';
/* eslint-disable */

import {
   Box,
   Button,
   Flex,
   IconButton,
   Table,
   Tbody,
   Td,
   Text,
   Th,
   Thead,
   Tr,
   useColorModeValue,
   useDisclosure,
   Select,
   Grid,
   useOutsideClick,
   useBreakpointValue,
   useColorMode,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import React, { useEffect, useRef, useState } from 'react';
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import VehicleMasterForm from './VehicleMasterForm';
import { useDispatch, useSelector } from 'react-redux';
import { deleteVehicle, getVehicle, getSingleVehicle, multiDeleteVehicle } from '../../../../../redux/actions/vehicle';
import DeleteDialog from '../../components/DeleteDialog';
import { downloadPDF, downloadExcel, usePagination, useFilterData } from '../../../../../utils/DataTableUtils';
import nodata from 'assets/img/nfts/no data.svg';
import { GridItem, Input, FormLabel } from '@chakra-ui/react';
import ViewSingleData from '../../components/ViewSingleData';
import { MdNavigateNext, MdNavigateBefore } from 'react-icons/md';
import DynamicTable from '../../components/DynamicAllDataTable';
import { SearchIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { useUser } from 'contexts';
import MobileDataTable from '../../components/MobileDataTable';
import { useGlobal } from '../../../../../contexts';
import Loader from 'views/admin/default/Loader';

const columnHelper = createColumnHelper();
const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

export default function VehicleMaster({ columnsData }) {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const backgroundColor = useColorModeValue('white', 'rgba(12, 20, 41, 1)');
   const [sorting, setSorting] = React.useState([]);
   const dispatch = useDispatch();
   const vehicleData = useSelector((state) => state.vehicle.list || []);
   const pagination = useSelector((state) => state.vehicle?.pagination);
   const [data, setData] = React.useState([...vehicleData]);
   const [currentRow, setCurrentRow] = React.useState(null);
   const [focusedInput, setFocusedInput] = React.useState(null);
   const [limit, setLimit] = React.useState(10);
   const [currentPage, setCurrentPage] = React.useState(1);
   const [searchQuery, setSearchQuery] = React.useState('');
   const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [isDeleteDialogOpen, setDeleteDialogOpen] = React.useState(false); // State to manage delete confirmation
   const [selectedRowId, setSelectedRowId] = React.useState(null);
   const [isEditingPage, setIsEditingPage] = useState(false);
   const [pageInputValue, setPageInputValue] = useState('');
   const [isLoading, setIsLoading] = useState(false);
   const [isEditMode, setIsEditMode] = React.useState(false);
   const [isOpenSingleView, setIsOpenSingleView] = React.useState(false);
   const singleEntity = useSelector((state) => state?.vehicle?.single || {});
   const isMobileOrTablet = useBreakpointValue({ base: true, md: false, lg: false });
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');
   const isDesktop = useBreakpointValue({ base: false, md: true });
   const { user } = useUser();
   const Admin = user?.isAdmin;

   const companyId = Number(localStorage.getItem('companyId'));
   const vehicleRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.vehicle
      : null;

   const { userGlobalSettingsData } = useGlobal();
   const globalSetting = userGlobalSettingsData?.[0]?.askCompany?.vehicle;
   const shouldShowCompanyDropdown = globalSetting || false;

   // Use the custom hooks
   const filteredData = useFilterData(data, searchQuery, columnsData); // Filtered data
   const { totalPages, getPaginationParams } = usePagination(data, filteredData); // Pagination

   useEffect(() => {
      setCurrentPage(1); // Reset to page 1 whenever searchQuery changes
   }, [searchQuery]);

   const debounce = (func, delay) => {
      let timer;
      return function (...args) {
         clearTimeout(timer);
         timer = setTimeout(() => func.apply(this, args), delay);
      };
   };

   const debouncedSearchHandler = React.useCallback(
      debounce((query) => {
         setDebouncedSearchQuery(query);
      }, 500),
      [],
   );

   // Handle search input changes
   const handleSearchChange = (e) => {
      const value = e.target.value.trimStart();
      setSearchQuery(value);
      debouncedSearchHandler(value);
   };

   useEffect(() => {
      const fetchMainData = async () => {
         try {
            setIsLoading(true);
            await dispatch(
               getVehicle({
                  page: currentPage,
                  // limit: limit === pagination?.totalProducts ? undefined : limit, // undefined fetches everything
                  search: debouncedSearchQuery,
                  sortBy: sorting.length ? sorting[0]?.name : 'name',
                  sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
                  ...(shouldShowCompanyDropdown && { companyIds: user?.loginhistory?.companyId }),
               }),
            );
         } catch (error) {
            console.error('Error fetching main data:', error);
         } finally {
            setIsLoading(false);
         }
      };

      fetchMainData();
   }, [dispatch, currentPage, limit, debouncedSearchQuery, sorting]);

   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeElement = document.activeElement;
         const isTyping =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.getAttribute('role') === 'textbox' ||
            activeElement?.isContentEditable;

         if (isTyping) return;

         if (e.key.toLowerCase() === 'n' || e.key === '+') {
            if (!isOpen && !isOpenSingleView && (Admin || vehicleRight?.add)) {
               e.preventDefault();
               setCurrentRow({});
               setIsEditMode(false);
               onOpen();
            }
         }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [!isOpen, !isOpenSingleView, Admin, vehicleRight?.add]);

   const handlePageChange = (directionOrPage) => {
      if (typeof directionOrPage === 'number') {
         // Direct page number navigation
         setCurrentPage(directionOrPage);
      } else {
         // Previous/next navigation
         const newPage = directionOrPage === 'next' ? pagination.nextPage : pagination.previousPage;
         if (newPage) setCurrentPage(newPage);
      }
   };

   const handlePageClick = () => {
      setIsEditingPage(true);
      setPageInputValue(''); // Clear input value when starting to edit
   };

   const handlePageInputSubmit = () => {
      const page = parseInt(pageInputValue);
      if (!isNaN(page) && page >= 1 && page <= pagination?.totalPages) {
         handlePageChange(page);
      }
      setIsEditingPage(false);
      setPageInputValue('');
   };

   const handlePageInputBlur = () => {
      handlePageInputSubmit();
   };

   const handlePageKeyDown = (e) => {
      if (e.key === 'Enter') {
         handlePageInputSubmit();
      } else if (e.key === 'Escape') {
         setIsEditingPage(false);
         setPageInputValue('');
      }
   };

   const handlePageSizeChange = (e) => {
      const value = e.target.value;

      // If "ALL" is selected, set limit to null or totalProducts to fetch everything
      const newLimit = value === 'ALL' ? pagination?.totalProducts : Number(value);

      setLimit(newLimit);
      setCurrentPage(1); // Reset to page 1 to avoid offset issues
   };

   const paginatedData = React.useMemo(() => {
      if (limit === pagination?.totalProducts) return vehicleData;

      if (!Array.isArray(vehicleData) || vehicleData.length === 0) {
         return [];
      }
      return vehicleData;
   }, [vehicleData, pagination?.currentPage, limit, pagination?.totalProducts]);
   useEffect(() => {
      setData([...vehicleData]);
   }, [vehicleData]);

   const handleDelete = async () => {
      if (selectedRowId !== null) {
         try {
            await dispatch(deleteVehicle(selectedRowId, companyId));
            setData(data.filter((row) => row.id !== selectedRowId));
            setDeleteDialogOpen(false); // Close the dialog after deletion
         } catch (err) {
            console.error('Failed to delete Vehicle:', err);
         }
      }
   };

   const handleDeleteClick = (id) => {
      setSelectedRowId(id);
      setDeleteDialogOpen(true);
   };

   const handleMutiDeleteClick = async (ids) => {
      if (ids.length > 0) {
         const payload = {
            ids: ids.map(Number),
            compId: user?.loginhistory?.companyId.toString(),
         };
         dispatch(multiDeleteVehicle(JSON.stringify(payload)));
         setDeleteDialogOpen(false); // Close the dialog after deletion
         await dispatch(
            getVehicle({
               page: currentPage,
               // limit: limit === pagination?.totalProducts ? undefined : limit, // undefined fetches everything
               search: debouncedSearchQuery,
               sortBy: sorting.length ? sorting[0]?.name : 'name',
               sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
               ...(shouldShowCompanyDropdown && { companyIds: user?.loginhistory?.companyId }),
            }),
         );
      }
   };

   const handleFocus = (id) => setFocusedInput(id);
   const handleBlur = () => setFocusedInput(null);
   const [columnVisibility, setColumnVisibility] = React.useState(
      columnsData.reduce((acc, column) => {
         acc[column.id] = true;
         return acc;
      }, {}),
   );

   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

   const columns = columnsData
      .filter((column) => columnVisibility[column.id])
      .map((column) =>
         columnHelper.accessor(column.id, {
            id: column.id,
            header: () => (
               <Text justifyContent="space-between" align="center" fontSize={{ sm: '10px', lg: '12px' }}>
                  {column.header}
               </Text>
            ),
            cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
         }),
      );

   const table = useReactTable({
      data,
      columns,
      state: { sorting },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   const handleSave = (formData) => {
      if (isEditMode && currentRow) {
         setData(data.map((row) => (row.id === currentRow.id ? { ...currentRow, ...formData } : row)));
      } else if (!isEditMode) {
         setData([...data, { ...formData, id: Date.now() }]);
      }
      onClose();
   };

   const handleViewSingleData = async (id) => {
      await dispatch(getSingleVehicle(id));
      setIsOpenSingleView(true);
   };

   const dynamicArray = [
      { label: 'Name', value: singleEntity?.name || '-' },
      { label: 'Vehicle No', value: singleEntity?.vehicleNo || '-' },
   ];

   const [expanded, setExpanded] = useState(false);
   const wrapperRef = useRef();
   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchQuery === '') setExpanded(false);
      },
   });

   return (
      <Box bg={backgroundColor} w="100%">
         <Flex
            width={{ base: '100%', md: 'auto' }}
            mb="8px"
            px={{ base: '1px', md: '25px' }}
            justifyContent="space-between"
            align="center"
            overflowX="auto"
            flexWrap="nowrap"
            gap={3}>
            {/* Left Section: Title + Add New Button */}
            <Flex align="center" gap={4} flexShrink={0}>
               <Text
                  color={textColor}
                  ml={{ base: '8px', md: 0 }}
                  fontSize={{ base: '17px', lg: '22px' }}
                  fontWeight="700"
                  lineHeight="100%">
                  Vehicle
               </Text>
            </Flex>

            {/* Right Section: Pagination + Limit + Search + Menu */}
            <Flex align="center" gap={2} flexShrink={0} p={3}>
               {!isMobileOrTablet && (Admin || vehicleRight?.add) && user && !user.isExpired && (
                  <button
                     onClick={() => {
                        setCurrentRow({});
                        setIsEditMode(false);
                        onOpen();
                     }}
                     className={`group relative flex items-center justify-center gap-3 py-2 px-7 rounded-xl font-medium shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 overflow-hidden border ${
                        isDark
                           ? 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-400/40 focus:ring-cyan-200/50 border-cyan-300/30'
                           : 'bg-gradient-to-br from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-white shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-400/40 focus:ring-emerald-200/70 border-emerald-300/50'
                     }`}>
                     {/* Gradient overlay on hover */}
                     <span
                        className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out ${
                           isDark ? 'via-cyan-100/20' : 'via-white/30'
                        }`}></span>

                     {/* Icon container with background */}
                     <span
                        className={`relative flex items-center justify-center w-6 h-6 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                           isDark ? 'bg-cyan-100/20 group-hover:bg-cyan-100/30' : 'bg-white/20 group-hover:bg-white/30'
                        }`}>
                        <svg
                           className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                        </svg>
                     </span>

                     <span className="relative text-base font-semibold">Add New</span>
                  </button>
               )}

               {/* Search Field */}
               <MotionFlex
                  ref={wrapperRef}
                  align="center"
                  bg={bg}
                  borderRadius="full"
                  cursor="pointer"
                  px={expanded ? 3 : 0}
                  w={expanded ? '200px' : '40px'}
                  h="40px"
                  transition="all 0.3s ease"
                  onClick={() => setExpanded(true)}>
                  <MotionSearchIcon
                     color={icon}
                     animate={{ rotate: expanded ? 0 : 90 }}
                     transition={{ duration: 0.3 }}
                     style={{
                        marginRight: expanded ? '8px' : '10px',
                        marginLeft: expanded ? '0' : '10px',
                     }}
                  />
                  {expanded && (
                     <Input
                        variant="unstyled"
                        placeholder="Search..."
                        value={searchQuery}
                        color={icon}
                        // onChange={(e) => setSearchQuery(e.target.value)}
                        onChange={handleSearchChange}
                        autoFocus
                     />
                  )}
               </MotionFlex>
            </Flex>

            {/* Mobile Floating Add New Button */}
            {isMobileOrTablet && (Admin || vehicleRight?.add) && user && !user.isExpired && (
               <Box position="fixed" bottom="4" right="4" zIndex={999}>
                  <button
                     onClick={() => {
                        setCurrentRow({});
                        setIsEditMode(false);
                        onOpen();
                     }}
                     className={`group relative flex items-center justify-center gap-3 py-2 px-7 rounded-xl font-medium shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 overflow-hidden border ${
                        isDark
                           ? 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-400/40 focus:ring-cyan-200/50 border-cyan-300/30'
                           : 'bg-gradient-to-br from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-white shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-400/40 focus:ring-emerald-200/70 border-emerald-300/50'
                     }`}>
                     {/* Gradient overlay on hover */}
                     <span
                        className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out ${
                           isDark ? 'via-cyan-100/20' : 'via-white/30'
                        }`}></span>

                     {/* Icon container with background */}
                     <span
                        className={`relative flex items-center justify-center w-6 h-6 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                           isDark ? 'bg-cyan-100/20 group-hover:bg-cyan-100/30' : 'bg-white/20 group-hover:bg-white/30'
                        }`}>
                        <svg
                           className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                        </svg>
                     </span>

                     <span className="relative text-base font-semibold">Add New</span>
                  </button>
               </Box>
            )}
         </Flex>

         {/* Table Rendering */}
         {isLoading ? (
            <Loader />
         ) : (
            <DynamicTable
               right={vehicleRight}
               table={table}
               data={paginatedData}
               handleViewSingleData={handleViewSingleData}
               emptyMessage="No vehicle vehicle found"
               onEditClick={(row) => {
                  setCurrentRow(row);
                  setIsEditMode(true);
                  onOpen();
                  setIsOpenSingleView(false);
               }}
               onDeleteClick={(id) => {
                  handleDeleteClick(id);
                  setIsOpenSingleView(false);
               }}
               onMultiDeleteClick={(ids) => {
                  handleMutiDeleteClick(ids);
               }}
               canDelete={true}
               columnVisibility={columnVisibility}
               columnsData={columnsData}
               sorting={sorting}
               limit={limit}
               setCurrentRow={setCurrentRow}
               onOpen={onOpen}
               setIsEditMode={setIsEditMode}
               setIsOpenSingleView={setIsOpenSingleView}
               setDeleteDialogOpen={setDeleteDialogOpen}
               setSelectedRowId={setSelectedRowId}
               debouncedSearchQuery={debouncedSearchQuery}
            />
         )}
         <VehicleMasterForm
            isOpen={isOpen}
            onClose={onClose}
            isEditMode={isEditMode}
            currentRow={currentRow}
            onSave={handleSave}
         />

         <style jsx>{`
            .editable-row {
               position: relative;
               background: ${useColorModeValue('white', 'gray.700')};
               transition: background 0.3s ease;
            }

            .editable-row:hover {
               background: ${useColorModeValue('gray.100', 'gray.600')};
            }

            .editable-row .edit-icon-container {
               display: none;
            }

            .editable-row:hover .edit-icon-container {
               display: block;
               position: absolute;
               right: 10px;
               top: 50%;
               transform: translateY(-50%);
            }
         `}</style>
         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="Vehicle" // dynamic title
         />
         {/* <ViewSingleData
            isOpenSingleView={isOpenSingleView}
            onClose={() => setIsOpenSingleView(false)}
            entityType="driver"
            singleEntity={singleEntity}
            dynamicArray={dynamicArray}
         /> */}
      </Box>
   );
}
